(ns admin.events.containers.curriculum-learner-pre-registration
  "Responsible for fetching data for the curriculum pre-reg (learner roster) page"
  (:require [kyber.router :refer [ensure-resource-v2]]
            [kyber.events.eql :as eql]
            [kyber.events.utils :refer [kyber-event-fx]]
            [re-frame.core :as re-frame]
            [admin.router.utils :as aru]
            [testdouble.cljs.csv :as csv]
            [kyber.lib.kyber-db :as kyber-db]))

(re-frame/reg-fx
 :curriculum.learners/download-csv
 (fn [{:keys [export-vals file-name]}]
   (let [comma-separated-vals (csv/write-csv export-vals)
         csv-blob (js/Blob. [comma-separated-vals] {:type "text/csv"})
         csv-url (-> js/window
                     .-URL
                     (.createObjectURL csv-blob))
         link (.createElement js/document "a")]
     (.setAttribute link "href" csv-url)
     (.setAttribute link "download" file-name)
     (set! (-> link .-style .-display) "none")
     (-> js/document .-body (.appendChild link))
     (.click link)
     (-> js/document .-body (.removeChild link))
     (-> js/window .-URL (.revokeObjectURL csv-url)))))

(defn- ->analytics-event [event]
  (keyword "curriculum-learner-preregistration" (name event)))

(defn- fmt-curriculum
  [{:curriculum/keys [uuid name learners valid-tags]}]
  {:curriculum/uuid uuid
   :curriculum/name name
   :curriculum/learners (->> learners
                            :curriculum.learners/edges
                            (mapv #(assoc % :curriculum/uuid uuid)))
   :curriculum.learners/page-info (:curriculum.learners/page-info learners)
   :curriculum/valid-tags valid-tags})

(defn fetch-export-data-success
  [{:keys [db]} [event-name response]]
  (let [curriculum (:>/curriculum response)
        csv-vals-vecs (mapv (fn [rec]
                              (-> rec
                                  (dissoc :user/uuid)
                                  vals
                                  vec)) (:curriculum.learners/sign-in-urls curriculum))]
    {:db db
     :curriculum.learners/download-csv {:export-vals csv-vals-vecs
                                        :file-name "roster.csv"}
     :dispatch-n [[:analytics/save-datapoint
                   {:event (->analytics-event event-name)
                    :event-version 0}]]}))

(kyber-event-fx ::fetch-export-data-success fetch-export-data-success)

(defn fetch-export-data-failure
  [_ [event-name _err]]
  {:dispatch-n [[:analytics/save-datapoint
                 {:event (->analytics-event event-name)
                  :event-version 0}]]})

(kyber-event-fx ::fetch-export-data-failure fetch-export-data-failure)

(defn fetch-export-data
  [{:keys [db]} [event-name context]]
  (let [{:keys [curriculum-uuid]} (:params context)
        params `[{(:>/curriculum {:curriculum/uuid ~curriculum-uuid})
                  [:curriculum/uuid
                   :curriculum/name
                   {:curriculum.learners/sign-in-urls
                    [:user/uuid
                     :user/email-address
                     :curriculum.learner/sign-in-url]}]}]]
    {:db db
     :dispatch-n [[:analytics/save-datapoint
                   {:event (->analytics-event event-name)
                    :event-version 0}]]
     :request
     (eql/request db
                  {:name event-name
                   :params params
                   :on-success [::fetch-export-data-success]
                   :on-failure [::fetch-export-data-failure]})}))

(kyber-event-fx ::fetch-export-data fetch-export-data)


(defn fetch-container-data-success
  [{:keys [db]} [event-name response]]
  (let [curriculum (:>/curriculum response)]
    {:db (kyber-db/tx-record db {:model :curriculum} (fmt-curriculum curriculum))
     :dispatch-n [[:analytics/save-datapoint
                   {:event (->analytics-event event-name)
                    :event-version 0}]]}))

(kyber-event-fx ::fetch-container-data-success fetch-container-data-success)

(defn fetch-container-data-failure
  [_ [event-name _err]]
  {:dispatch-n [[:analytics/save-datapoint
                 {:event (->analytics-event event-name)
                  :event-version 0}]]})

(kyber-event-fx ::fetch-container-data-failure fetch-container-data-failure)

(defn fetch-container-data
  [{:keys [db]} [event-name context]]
  (let [{:keys [current-page page-size sort-order sort-key email-address]
         :or {page-size 10
              current-page 0
              sort-order :desc
              sort-key :curriculum_registration_created_at}}
        (aru/xform-query
         {:query (:query context)
          :xforms {:page-size js/parseInt
                   :current-page js/parseInt
                   :sort-order keyword
                   :sort-key keyword}})
        
        email-address-query (or email-address
                                (get-in context [:params :email-address]))

        {:keys [curriculum-uuid]} (:params context)
        params `[{(:>/curriculum {:curriculum/uuid ~curriculum-uuid})
                  [:curriculum/uuid
                   :curriculum/name
                   {:curriculum/valid-tags
                    [:content-provider.tag-group/uuid
                     :content-provider.tag-group/name
                     :content-provider.tag-group/description
                     {:content-provider.tag-group/values
                      [:content-provider.tag/uuid
                       :content-provider.tag/name]}]}
                   {(:curriculum/learners {:limit ~page-size
                                           :offset ~(* current-page page-size)
                                           :sort-order ~sort-order
                                           :sort-key ~sort-key
                                           :email-address ~email-address-query})
                    [:curriculum.learners/page-info
                     {:curriculum.learners/edges
                      [{:curriculum/registration
                        [:content-provider.tag-group/uuid
                         :content-provider.tag-group/name
                         :content-provider.tag/uuid
                         :content-provider.tag/name]}
                       :curriculum-registration/created-at
                       :user/uuid
                       :user/email-address
                       :user/family-name
                       :user/given-name]}]}]}]]
    {:db (let [all-curriculum-learners
               (as-> db $
                 (kyber-db/get-sort $ :curriculum-learner)
                 (kyber-db/get-records db
                                       :curriculum-learner
                                       $))]
           (-> db
               (kyber-db/retract-records
                {:model :curriculum-learner}
                all-curriculum-learners)
               (kyber-db/retract-refs
                {:model :curriculum
                 :refs-key :curriculum/learners
                 :all? true}
                {:curriculum/uuid curriculum-uuid})))
     :dispatch-n [[:analytics/save-datapoint
                   {:event (->analytics-event event-name)
                    :event-version 0}]]
     :request
     (eql/request db
                  {:name event-name
                   :params params
                   :on-success [::fetch-container-data-success]
                   :on-failure [::fetch-container-data-failure]})}))

(kyber-event-fx ::fetch-container-data fetch-container-data)

(def ensure-curriculum-learners
  ""
  (partial
   ensure-resource-v2
   ::fetch-container-data
   #(re-frame/dispatch [::fetch-container-data {:params %1 :query %2}])))
